import Api from "./api.service";
import humps from "humps";
import { isEmpty } from "lodash";

const DEFAULT_INCLUDES =
  "product_prices,product_bundles.bundle,product_license_bundles.license_bundle,product_groupings.product_group,product_metadata_items.product_metadata_type";
class Products {
  static getProducts(tenantSlug, params) {
    const { withoutIncludes, ...restParams } = params;
    if (!withoutIncludes) {
      restParams.include = DEFAULT_INCLUDES;
    }
    return Api.get(`/api/v1/tenants/${tenantSlug}/products`, {
      params: restParams
    });
  }

  static filterProducts(tenantSlug, params) {
    params = humps.decamelizeKeys(params);
    return Api.get(`/api/v1/tenants/${tenantSlug}/products`, {
      params: {
        sort: params.sort,
        include:
          "product_prices,product_bundles.bundle,product_license_bundles.license_bundle,product_groupings.product_group,product_metadata_items.product_metadata_type",
        filter: params.filter
      }
    });
  }

  static filterAddOns(tenantSlug) {
    return Api.get(`/api/v1/tenants/${tenantSlug}/products`, {
      params: {
        filter: { add_on: false },
        fields: { products: "name" }
      }
    });
  }

  static get(tenantSlug, slug) {
    return Api.get(`/api/v1/tenants/${tenantSlug}/products/${slug}`, {
      params: {
        include:
          "product_prices,product_bundles.bundle,product_license_bundles.license_bundle,product_groupings.product_group,product_metadata_items.product_metadata_type,template,add_ons"
      }
    });
  }

  static getDiscount(tenantSlug, slug, customerSlug) {
    customerSlug = humps.decamelizeKeys(encodeURIComponent(customerSlug));
    return Api.get(`/api/v1/tenants/${tenantSlug}/products/${slug}`, {
      params: {
        include:
          "product_prices,product_bundles.bundle,product_license_bundles.license_bundle,product_groupings.product_group,product_metadata_items.product_metadata_type,template",
        with_discounts: customerSlug
      }
    });
  }

  static create(tenantSlug, data) {
    const { name, visible, renewal, archived, netsuiteIdentifier, netsuiteDescription, oneTimePeriod, addOn } = data.product;
    const { productGroupings, template } = data;

    const payload = {
      data: {
        type: "products",
        attributes: {
          name,
          visible,
          renewal,
          netsuite_identifier: netsuiteIdentifier,
          archived,
          one_time_period: oneTimePeriod,
          netsuite_description: netsuiteDescription,
          add_on: addOn
        },
        relationships: {
          product_groups: {
            data: productGroupings.map(groupSlug => ({
              type: "product_groups",
              id: groupSlug
            }))
          },
          template: !isEmpty(template)
            ? {
                data: {
                  type: "templates",
                  id: template.id
                }
              }
            : {}
        }
      }
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/products`, payload);
  }

  static update(slug, tenantSlug, data) {
    const { name, visible, renewal, netsuiteIdentifier, netsuiteDescription, addOn } = data.product;
    const { productGroupings, groupingDecisions, template } = data;
    const grouping_decisions = humps.decamelizeKeys(groupingDecisions);

    const payload = {
      data: {
        type: "products",
        id: slug,
        attributes: {
          name,
          visible,
          renewal,
          netsuite_identifier: netsuiteIdentifier,
          netsuite_description: netsuiteDescription,
          grouping_decisions,
          add_on: addOn
        },
        relationships: {
          product_groups: {
            data: productGroupings.map(groupSlug => ({
              type: "product_groups",
              id: groupSlug
            }))
          },
          template: !isEmpty(template)
            ? {
                data: {
                  type: "templates",
                  id: template.id
                }
              }
            : {}
        }
      }
    };

    return Api.put(`/api/v1/tenants/${tenantSlug}/products/${slug}`, payload);
  }

  static toggleArchived(slug, tenantSlug, data) {
    const { archived } = data.product;
    const payload = {
      data: {
        type: "products",
        id: slug,
        attributes: {
          archived
        }
      }
    };
    return Api.put(`/api/v1/tenants/${tenantSlug}/products/${slug}`, payload);
  }

  static createPrice(productSlug, tenantSlug, productPrice) {
    const { amount, currency, term } = productPrice;
    const payload = {
      data: {
        type: "product_prices",
        attributes: {
          amount,
          currency,
          term
        },
        relationships: {
          product: {
            data: {
              id: productSlug,
              type: "products"
            }
          }
        }
      }
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/product_prices`, payload);
  }

  static updatePrice(id, tenantSlug, data) {
    const { amount, currency, term } = data;

    const payload = {
      data: {
        type: "product_prices",
        id,
        attributes: {
          amount,
          currency,
          term
        }
      }
    };
    return Api.put(`/api/v1/tenants/${tenantSlug}/product_prices/${id}`, payload);
  }

  static deletePrice(id, tenantSlug) {
    return Api.delete(`/api/v1/tenants/${tenantSlug}/product_prices/${id}`);
  }

  static createBundleLink(tenantSlug, productSlug, data) {
    const { size, term, visible, bundleSlug } = data;
    const payload = {
      data: {
        type: "product_bundles",
        attributes: {
          size,
          term,
          visible
        },
        relationships: {
          product: {
            data: {
              id: productSlug,
              type: "products"
            }
          },
          bundle: {
            data: {
              id: bundleSlug,
              type: "bundles"
            }
          }
        }
      }
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/product_bundles`, payload);
  }

  static createLicenseBundleLink(tenantSlug, productSlug, data) {
    const { size, term, visible, bundleSlug } = data;
    const payload = {
      data: {
        type: "product_license_bundles",
        attributes: {
          size,
          term,
          visible
        },
        relationships: {
          product: {
            data: {
              id: productSlug,
              type: "products"
            }
          },
          license_bundle: {
            data: {
              id: bundleSlug,
              type: "license_bundles"
            }
          }
        }
      }
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/product_license_bundles`, payload);
  }

  static updateBundleLink(tenantSlug, data) {
    const { id, visible, size, term } = data;
    const payload = {
      data: {
        id,
        type: "product_bundles",
        attributes: { visible, size, term }
      }
    };

    return Api.put(`/api/v1/tenants/${tenantSlug}/product_bundles/${id}`, payload);
  }

  static removeBundleLink(id, tenantSlug) {
    return Api.delete(`/api/v1/tenants/${tenantSlug}/product_bundles/${id}`);
  }

  static updateLicenseBundleLink(tenantSlug, data) {
    const { id, visible, size, term } = data;
    const payload = {
      data: {
        id,
        type: "product_license_bundles",
        attributes: { visible, size, term }
      }
    };

    return Api.put(`/api/v1/tenants/${tenantSlug}/product_license_bundles/${id}`, payload);
  }

  static removeLicenseBundleLink(id, tenantSlug) {
    return Api.delete(`/api/v1/tenants/${tenantSlug}/product_license_bundles/${id}`);
  }

  static createFeatureLink(tenantSlug, productSlug, data) {
    const { value, visible, featureSlug } = data;
    const payload = {
      data: {
        type: "product_metadata_items",
        attributes: {
          value,
          visible
        },
        relationships: {
          product: {
            data: {
              id: productSlug,
              type: "products"
            }
          },
          product_metadata_type: {
            data: {
              id: featureSlug,
              type: "product_metadata_types"
            }
          }
        }
      }
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/product_metadata_items`, payload);
  }

  static updateFeatureLink(tenantSlug, data) {
    const { id, visible, value } = data;
    const payload = {
      data: {
        id,
        type: "product_metadata_items",
        attributes: {
          visible,
          value
        }
      }
    };

    return Api.put(`/api/v1/tenants/${tenantSlug}/product_metadata_items/${id}`, payload);
  }

  static removeFeatureLink(id, tenantSlug) {
    return Api.delete(`/api/v1/tenants/${tenantSlug}/product_metadata_items/${id}`);
  }

  static createProductAddOn(tenantSlug, productSlug, data) {
    const payload = {
      product_slug: data,
      add_on_slug: productSlug
    };

    return Api.post(`/api/v1/tenants/${tenantSlug}/product_add_ons`, payload);
  }

  static removeProductAddOn(tenantSlug, id) {
    return Api.delete(`/api/v1/tenants/${tenantSlug}/product_add_ons/${id}`);
  }

  static availableForCustomer(tenantSlug, customerSlug, params) {
    params = humps.decamelizeKeys(params);
    return Api.get(`/api/v1/tenants/${tenantSlug}/products`, {
      params: {
        include:
          "product_prices,product_bundles.bundle,product_license_bundles.license_bundle,product_metadata_items.product_metadata_type",
        filter: params.filter
      }
    });
  }

  static filterProductBundles(tenantSlug, params) {
    params = humps.decamelizeKeys(params);
    return Api.get(`/api/v1/tenants/${tenantSlug}/product_bundles`, {
      params: params
    });
  }

  static filterProductLicenseBundles(tenantSlug, params) {
    params = humps.decamelizeKeys(params);
    return Api.get(`/api/v1/tenants/${tenantSlug}/product_license_bundles`, {
      params: params
    });
  }

  static filterProductsAddOns(tenantSlug) {
    return Api.get(`/api/v1/tenants/${tenantSlug}/products`, {
      params: {
        filter: { archived: false, add_on: false },
        fields: { products: "id,name" }
      }
    });
  }
}

const ProductsService = Object.freeze(Products);
export default ProductsService;
